import { Box, Flex } from "@chakra-ui/react";
import {
  type BridgeDirection,
  type LiFiQuoteResponse,
  type QuoteProvider,
  type ZeroXQuoteResponse,
} from "@umami/swap";
import { useState } from "react";

import {
  ConversionRateDisplay,
  MinimumReceivedDisplay,
  NetworkFeeDisplay,
  PriceImpactDisplay,
  SlippageControls,
} from "./components";
import { useFeeEstimation } from "./hooks/useFeeEstimation";

import { useBridge } from "@/hooks/useBridge";
import { useColor } from "@/styles/useColor";

interface SwapInfoProps {
  sellTokenSymbol: string;
  buyTokenSymbol: string;
  conversionRate: number;
  fiatValue: string;
  priceImpact: number;
  onSlippageChange: (value: number) => void;
  defaultSlippage?: number;
  showConversionRate?: boolean;
  showSlippageControls?: boolean;
  minimumReceived?: string;
  direction: BridgeDirection;
  showPriceImpactAndSlippage?: boolean;
  showNetworkFee?: boolean;
  onFeeEstimateChange?: (
    feeEstimate: {
      nativeFee: bigint;
      zroFee: bigint;
      nativeFees?: Array<{
        amount: bigint;
        symbol: string;
        chainId?: string;
      }>;
    } | null
  ) => void;
  zeroXQuote?: ZeroXQuoteResponse | null;
  lifiQuote?: LiFiQuoteResponse | null;
  layerZeroQuote?: any | null; // LayerZeroBridgeQuoteResponse
  provider?: QuoteProvider;
  sellToken?: any;
  buyToken?: any;
}

export const SwapInfo = ({
  sellTokenSymbol,
  buyTokenSymbol,
  conversionRate,
  fiatValue,
  priceImpact,
  onSlippageChange,
  defaultSlippage = 0.5,
  showConversionRate = true,
  showSlippageControls = true,
  minimumReceived,
  direction,
  showPriceImpactAndSlippage = false,
  showNetworkFee = true,
  onFeeEstimateChange,
  zeroXQuote,
  lifiQuote,
  layerZeroQuote,
  provider,
  sellToken,
  buyToken,
}: SwapInfoProps) => {
  const color = useColor();
  const { estimateFee } = useBridge();
  const [showSlippageOptions, setShowSlippageOptions] = useState(false);
  const [customSlippage, setCustomSlippage] = useState<string>("0.5");
  const [selectedSlippage, setSelectedSlippage] = useState<"auto" | "0.5" | "3" | "custom">("auto");

  const { feeDisplay, isLoading: isLoadingFee } = useFeeEstimation({
    direction,
    provider,
    zeroXQuote,
    lifiQuote,
    layerZeroQuote,
    estimateFee,
    showNetworkFee,
    onFeeEstimateChange,
    sellToken,
    buyToken,
  });

  const handleSlippageChange = (value: string) => {
    if (value === "auto") {
      setSelectedSlippage("auto");
      setCustomSlippage("");
      onSlippageChange(defaultSlippage);
    } else {
      const numericValue = parseFloat(value);

      if (!numericValue) {
        setCustomSlippage("");
        setSelectedSlippage("auto");
      } else {
        setSelectedSlippage(value as "0.5" | "3" | "custom");
        setCustomSlippage(value);
        onSlippageChange(numericValue);
      }
    }
  };

  const handleToggleSlippageOptions = () => {
    setShowSlippageOptions(!showSlippageOptions);
  };

  if (!showNetworkFee && !showPriceImpactAndSlippage && !showConversionRate && !minimumReceived) {
    return null;
  }

  return (
    <Box padding="16px" background={color("100")} borderRadius="8px">
      {showConversionRate && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          marginBottom={showNetworkFee ? "3" : "0"}
        >
          <ConversionRateDisplay
            buyTokenSymbol={buyTokenSymbol}
            conversionRate={conversionRate}
            fiatValue={fiatValue}
            sellTokenSymbol={sellTokenSymbol}
          />
        </Flex>
      )}

      {showNetworkFee && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          marginBottom={showPriceImpactAndSlippage ? "2" : "0"}
        >
          <NetworkFeeDisplay
            feeDisplay={feeDisplay}
            isLoading={isLoadingFee}
            lifiQuote={lifiQuote}
            provider={provider}
          />
        </Flex>
      )}

      {showPriceImpactAndSlippage && (
        <>
          <Flex alignItems="center" justifyContent="space-between" marginBottom="2">
            <PriceImpactDisplay priceImpact={priceImpact} />
          </Flex>

          <Flex alignItems="center" justifyContent="space-between">
            <SlippageControls
              customSlippage={customSlippage}
              defaultSlippage={defaultSlippage}
              onSlippageChange={handleSlippageChange}
              onToggleSlippageOptions={handleToggleSlippageOptions}
              selectedSlippage={selectedSlippage}
              showSlippageControls={showSlippageControls}
              showSlippageOptions={showSlippageOptions}
            />
          </Flex>
        </>
      )}

      {minimumReceived && (
        <Flex alignItems="center" justifyContent="space-between" marginTop="2">
          <MinimumReceivedDisplay
            buyTokenSymbol={buyTokenSymbol}
            minimumReceived={minimumReceived}
          />
        </Flex>
      )}
    </Box>
  );
};
